import React, { useEffect, useState, useCallback } from 'react';
import { useT } from '../../context/TenantContext';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import { useAuth } from '../../context/AuthContext';
import { useRealtime } from '../../hooks/useRealtime';
import { Button } from '../../components/ui';

const ROLE_OPTIONS = ['admin','agent','viewer','receptionist'];
const LEVEL_OPTIONS = [1,2,3,4,5];
const ROLE_C = { admin:'#B91C1C', agent:'#0085CA', viewer:'#6B7280', receptionist:'#7C3AED' };
const ROLE_BG = { admin:'#FEF2F2', agent:'#E6F4FB', viewer:'#F3F4F6', receptionist:'#EDE9FE' };

function Modal({ title, onClose, children }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:16,padding:28,width:'100%',maxWidth:480,boxShadow:'0 20px 60px rgba(0,0,0,.2)',maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
          <h2 style={{fontSize:17,fontWeight:700,color:'#111827'}}>{title}</h2>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9CA3AF',lineHeight:1}}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div style={{marginBottom:14}}>
      <label style={{display:'block',fontSize:11,fontWeight:600,letterSpacing:.4,color:'#6B7280',marginBottom:6,textTransform:'uppercase'}}>
        {label}{required && <span style={{color:'#B91C1C'}}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width:'100%',height:40,padding:'0 12px',
  background:'#fff',border:'1.5px solid #E5E7EB',
  borderRadius:8,fontSize:14,color:'#111827',outline:'none',
  fontFamily:'var(--font-body)',
};

export default function Agents() {
  const { t } = useT();
  const { agent: me }   = useAuth();
  const isAdmin         = me?.role === 'admin' || me?.role === 'superadmin';
  const [agents,        setAgents]        = useState([]);
  const [onlineIds,     setOnlineIds]     = useState([]);
  const [agentStats,    setAgentStats]    = useState({});
  const [loading,       setLoading]       = useState(true);
  const [showModal,     setShowModal]     = useState(false);
  const [editAgent,     setEditAgent]     = useState(null); // null = crear nuevo
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const [form,          setForm]          = useState({ username:'', email:'', full_name:'', role:'agent', max_level:1, password:'', active:true });

  const token = localStorage.getItem('hd_token');
  const headers = { 'Content-Type':'application/json', Authorization:`Bearer ${token}` };

  async function load() {
    const calls = [
      fetch('/api/agents',  { headers }).then(r=>r.json()),
      fetch('/api/realtime/online', { headers }).then(r=>r.json()).catch(()=>[]),
    ];
    if (isAdmin) calls.push(
      fetch('/api/agents/stats', { headers }).then(r=>r.json()).catch(()=>[])
    );
    const [agts, online, stats] = await Promise.all(calls);
    setAgents(agts);
    setOnlineIds((online||[]).map(o=>o.agentId));
    if (stats) {
      const map = {};
      stats.forEach(s => { map[s.id] = s; });
      setAgentStats(map);
    }
  }

  useEffect(() => { load().finally(()=>setLoading(false)); }, []);

  // Actualizar onlineIds en tiempo real sin recargar la página
  useRealtime(useCallback((ev) => {
    if (ev.type === 'agents_online') {
      setOnlineIds((ev.agents || []).map(o => o.agentId));
    }
  }, []));

  function openCreate() {
    setEditAgent(null);
    setForm({ username:'', email:'', full_name:'', role:'agent', max_level:1, password:'', active:true });
    setError('');
    setShowModal(true);
  }

  function openEdit(a) {
    setEditAgent(a);
    setForm({ username:a.username, email:a.email, full_name:a.full_name, role:a.role, max_level:a.max_level, password:'', active:!!a.active });
    setError('');
    setShowModal(true);
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      const url  = editAgent ? `/api/agents/${editAgent.id}` : '/api/agents';
      const method = editAgent ? 'PUT' : 'POST';
      const body = { ...form };
      if (editAgent && !body.password) delete body.password;

      const res  = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar');
      setShowModal(false);
      load();
    } catch(e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(a) {
    await fetch(`/api/agents/${a.id}`, {
      method:'PUT', headers,
      body: JSON.stringify({ active: !a.active })
    });
    load();
  }

  async function handleDelete(a) {
    if (!confirm(`¿Eliminar al agente "${a.full_name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await fetch(`/api/agents/${a.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('hd_token')}` } });
      load();
    } catch(e) {
      alert(e.message || 'Error al eliminar el agente');
    }
  }

  const initials = (name) => name?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() || '??';
  const onlineCount = agents.filter(a=>onlineIds.includes(a.id)).length;

  return (
    <BackDeskLayout title={t('agents.title')}
      actions={
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:12,fontWeight:600,padding:'4px 12px',borderRadius:20,background:'#F0FDF4',color:'#15803D',border:'1px solid #86EFAC'}}>
            ● {onlineCount} en línea
          </span>
          {isAdmin && (
            <Button variant="primary" onClick={openCreate}>+ Nuevo agente</Button>
          )}
        </div>
      }>

      {loading
        ? <div className="bd-loading"><div className="bd-spinner-sm"/>Cargando…</div>
        : (
          <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,overflow:'hidden',boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}>
            <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',background:'#FAFAFA'}}>
              <span style={{fontSize:13,fontWeight:600}}>{agents.length} agentes registrados</span>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'#FAFAFA'}}>
                  {['Agente','Rol / Nivel','Tickets abiertos','Resueltos','T. medio','Estado',isAdmin?'Acciones':''].map(h=>(
                    <th key={h} style={{padding:'10px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:.4,textTransform:'uppercase',color:'#9CA3AF',borderBottom:'1px solid #F3F4F6'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {agents.map(a => {
                  const isOnline = onlineIds.includes(a.id);
                  const st = agentStats[a.id];
                  return (
                    <tr key={a.id} style={{borderBottom:'1px solid #F9FAFB'}}>
                      <td style={{padding:'12px 16px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <div style={{position:'relative',flexShrink:0}}>
                            <div style={{width:36,height:36,borderRadius:'50%',background:'#0085CA',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,opacity:a.active?1:.4}}>
                              {initials(a.full_name)}
                            </div>
                            <div style={{position:'absolute',bottom:0,right:0,width:10,height:10,borderRadius:'50%',background:isOnline?'#15803D':'#D1D5DB',border:'2px solid #fff'}}/>
                          </div>
                          <div>
                            <div style={{fontSize:13,fontWeight:600,color:a.active?'#111827':'#9CA3AF'}}>{a.full_name}</div>
                            <div style={{fontSize:11,color:'#9CA3AF'}}>{a.username}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{padding:'12px 16px'}}>
                        <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:10,color:ROLE_C[a.role],background:ROLE_BG[a.role]}}>{a.role}</span>
                        <span style={{fontSize:11,color:'#9CA3AF',marginLeft:6}}>N1–N{a.max_level}</span>
                      </td>
                      <td style={{padding:'12px 16px'}}>
                        <span style={{fontSize:14,fontWeight:700,color:st?.tickets_open>5?'#B91C1C':st?.tickets_open>2?'#B45309':'#111827'}}>
                          {st?.tickets_open ?? 0}
                        </span>
                      </td>
                      <td style={{padding:'12px 16px',fontSize:13,color:'#374151'}}>{st?.tickets_resolved ?? 0}</td>
                      <td style={{padding:'12px 16px',fontSize:13,color:'#374151'}}>
                        {st?.avg_resolution_min ? `${Math.round(st.avg_resolution_min/60)}h` : '—'}
                      </td>
                      <td style={{padding:'12px 16px'}}>
                        <span style={{fontSize:11,fontWeight:600,color:isOnline?'#15803D':a.active?'#6B7280':'#B91C1C'}}>
                          {isOnline ? '● En línea' : a.active ? '○ Disponible' : '✕ Inactivo'}
                        </span>
                      </td>
                      {isAdmin && (
                        <td style={{padding:'12px 16px'}}>
                          <div style={{display:'flex',gap:6}}>
                            <Button variant="ghost" style={{height:30,fontSize:11}} onClick={() => openEdit(a)}>{t('general.edit')}</Button>
                            <Button
                              variant={a.active ? 'danger' : 'success'}
                              style={{height:30,fontSize:11}}
                              onClick={() => toggleActive(a)}>
                              {a.active ? 'Desactivar' : 'Activar'}
                            </Button>
                            {a.id !== me?.id && (
                              <Button variant="danger" style={{height:30,fontSize:11}}
                                onClick={() => handleDelete(a)}>
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      }

      {/* Modal crear/editar */}
      {showModal && (
        <Modal title={editAgent ? `Editar — ${editAgent.full_name}` : 'Nuevo agente'} onClose={() => setShowModal(false)}>
          {error && <div style={{padding:'10px 14px',marginBottom:14,background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,color:'#B91C1C',fontSize:13}}>{error}</div>}

          <Field label="Nombre completo" required>
            <input style={inputStyle} value={form.full_name} onChange={e=>setForm(f=>({...f,full_name:e.target.value}))} placeholder="Ej. Ana García López"/>
          </Field>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Usuario" required>
              <input style={{...inputStyle,fontFamily:'monospace'}} value={form.username} onChange={e=>setForm(f=>({...f,username:e.target.value}))} placeholder="a.garcia" disabled={!!editAgent}/>
            </Field>
            <Field label={editAgent ? 'Nueva contraseña' : 'Contraseña'} required={!editAgent}>
              <input style={inputStyle} type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder={editAgent?'Dejar vacío para no cambiar':'Mínimo 8 caracteres'}/>
            </Field>
          </div>
          <Field label="Email" required>
            <input style={inputStyle} type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="a.garcia@uneatlantico.es"/>
          </Field>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Rol">
              <select style={{...inputStyle,cursor:'pointer'}} value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
                {ROLE_OPTIONS.map(r=><option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Nivel máximo">
              <select style={{...inputStyle,cursor:'pointer'}} value={form.max_level} onChange={e=>setForm(f=>({...f,max_level:Number(e.target.value)}))}>
                {LEVEL_OPTIONS.map(l=><option key={l} value={l}>N{l}</option>)}
              </select>
            </Field>
          </div>
          {editAgent && (
            <Field label="Estado">
              <div style={{display:'flex',gap:8}}>
                {[true,false].map(v=>(
                  <button key={String(v)} type="button"
                    onClick={()=>setForm(f=>({...f,active:v}))}
                    style={{flex:1,height:38,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',
                      background:form.active===v?(v?'#F0FDF4':'#FEF2F2'):'#fff',
                      color:form.active===v?(v?'#15803D':'#B91C1C'):'#6B7280',
                      border:`1.5px solid ${form.active===v?(v?'#86EFAC':'#FECACA'):'#E5E7EB'}`}}>
                    {v ? 'Activo' : 'Inactivo'}
                  </button>
                ))}
              </div>
            </Field>
          )}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:20,paddingTop:16,borderTop:'1px solid #F3F4F6'}}>
            <Button variant="ghost" onClick={()=>setShowModal(false)}>{t('general.cancel')}</Button>
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : editAgent ? 'Guardar cambios' : 'Crear agente'}
            </Button>
          </div>
        </Modal>
      )}
    </BackDeskLayout>
  );
}
